import * as React from 'react';

const BOLD_TAG_PATTERN = /<\/?(b|strong)>/gi;

const hasBalancedBoldTags = (text: string): boolean => {
  const tags: string[] = [];
  let match: RegExpExecArray | null;

  BOLD_TAG_PATTERN.lastIndex = 0;
  while ((match = BOLD_TAG_PATTERN.exec(text)) !== null) {
    const tag = match[1].toLowerCase();
    const isClosingTag = match[0][1] === '/';

    if (isClosingTag) {
      if (tags.pop() !== tag) return false;
    } else {
      tags.push(tag);
    }
  }

  return tags.length === 0;
};

/**
 * Renders CMS result text, turning only attribute-free <b>/<strong> pairs
 * into <strong>. Everything else stays literal text, escaped by React.
 */
export const formatResultText = (text?: string | null): React.ReactNode[] => {
  if (!text) return [];
  if (!hasBalancedBoldTags(text)) return [text];

  const nodes: React.ReactNode[] = [];
  const nodeStack: React.ReactNode[][] = [nodes];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  BOLD_TAG_PATTERN.lastIndex = 0;
  while ((match = BOLD_TAG_PATTERN.exec(text)) !== null) {
    const currentNodes = nodeStack[nodeStack.length - 1];
    if (match.index > lastIndex) {
      currentNodes.push(text.slice(lastIndex, match.index));
    }

    if (match[0][1] === '/') {
      const boldNodes = nodeStack.pop() as React.ReactNode[];
      nodeStack[nodeStack.length - 1].push(
        <strong key={match.index}>
          <em>{boldNodes}</em>
        </strong>
      );
    } else {
      nodeStack.push([]);
    }

    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodeStack[nodeStack.length - 1].push(text.slice(lastIndex));
  }

  return nodes;
};

/** Plain-text version (bold tags removed) for aria-live announcements. */
export const stripResultMarkup = (text?: string | null): string =>
  text && hasBalancedBoldTags(text)
    ? text.replace(BOLD_TAG_PATTERN, '')
    : text || '';
